import LoadObject from "./LoadObject";
/**
 * Copyright (c) 2014-present, Facebook, Inc.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree. An additional grant
 * of patent rights can be found in the PATENTS file in the same directory.
 *
 * @flow
 */

/**
 * This helps work with state that can be represented by a single load object.
 * Similar to LoadObjectMap.
 */
class LoadObjectState<V> {
    private _data: LoadObject<V>;
    private _load: () => void;
    private _shouldLoad: (lo: LoadObject<V>) => boolean;

    private _preventLoadsForThisFrame: boolean;
    private _clearPreventLoadsForThisFrame: number | null;

    constructor(load: () => void, shouldLoad?: (lo: LoadObject<V>) => boolean)
    {
        this._data = LoadObject.empty();
        this._load = load;
        this._shouldLoad = shouldLoad || (lo => lo.isEmpty());
        this._preventLoadsForThisFrame = false;
        this._clearPreventLoadsForThisFrame = null;
    }

    public getLoadObject(): LoadObject<V>
    {
        if (!this._preventLoadsForThisFrame && this._shouldLoad(this._data))
        {
            this._preventLoadsForThisFrame = true;
            if (this._clearPreventLoadsForThisFrame)
            {
                clearTimeout(this._clearPreventLoadsForThisFrame);
            }

            this._clearPreventLoadsForThisFrame = window.setTimeout(
                () =>
                {
                    this._load();
                    this._preventLoadsForThisFrame = false;
                    this._clearPreventLoadsForThisFrame = null;
                },
                0,
            );
        }
        return this._data;
    }

    public setLoadObject(lo: LoadObject<V>): LoadObjectState<V>
    {
        if (lo === this._data)
        {
            return this;
        }
        const next = new LoadObjectState(this._load, this._shouldLoad);
        next._data = lo;
        return next;
    }

    public map(fn: (value: V) => V): LoadObjectState<V>
    {
        const lo = this.getLoadObject().map(fn);
        if (lo === this._data)
        {
            return this;
        }
        const next = new LoadObjectState(this._load, this._shouldLoad);
        next._data = lo;
        return next;
    }
}

export default LoadObjectState;
